import React, { useState } from 'react'
import { 
  Target, 
  Users, 
  MapPin, 
  Plus, 
  X, 
  Eye,
  TrendingUp,
  Shield,
  CheckCircle2,
  Building,
  Award,
  Zap,
  Star,
  AlertTriangle,
  Lightbulb,
  Search,
  Globe,
  DollarSign
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { getIndustryConfig } from '../industry-configs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Input } from '../../../components/ui/Input'
import { Label } from '../../../components/ui/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select'
import { Checkbox } from '../../../components/ui/Checkbox'
import { Button } from '../../../components/ui/Button'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { cn } from '../../../lib/utils'

export const CompetitiveIntelligenceScreen: React.FC = () => {
  const { data, updateData } = useOnboardingStore()
  const [newCompetitor, setNewCompetitor] = useState('')
  
  if (!data.selectedIndustry) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Industry Selection Required</h3>
          <p className="text-gray-500">Please select an industry first to configure your competitive intelligence.</p>
        </div>
      </div>
    )
  }
  
  const industryConfig = getIndustryConfig(data.selectedIndustry)

  const updateServiceArea = (field: string, value: string) => {
    updateData('competitiveProfile', {
      ...data.competitiveProfile,
      serviceArea: {
        ...data.competitiveProfile.serviceArea,
        [field]: value
      }
    })
  }

  const addCompetitor = () => {
    if (newCompetitor && newCompetitor.trim()) {
      updateData('competitiveProfile', {
        ...data.competitiveProfile,
        knownCompetitors: [...data.competitiveProfile.knownCompetitors, newCompetitor.trim()]
      })
      setNewCompetitor('')
    }
  }

  const removeCompetitor = (index: number) => {
    const updatedCompetitors = data.competitiveProfile.knownCompetitors.filter((_, i) => i !== index)
    updateData('competitiveProfile', {
      ...data.competitiveProfile,
      knownCompetitors: updatedCompetitors
    })
  }

  const toggleAdvantage = (advantage: string) => {
    const currentAdvantages = data.competitiveProfile.competitiveAdvantages
    const updatedAdvantages = currentAdvantages.includes(advantage)
      ? currentAdvantages.filter(a => a !== advantage)
      : [...currentAdvantages, advantage]
    
    updateData('competitiveProfile', {
      ...data.competitiveProfile,
      competitiveAdvantages: updatedAdvantages
    })
  }

  const radiusOptions = [
    { value: '5', label: '5 miles', description: 'Local neighborhood' },
    { value: '10', label: '10 miles', description: 'City area' },
    { value: '25', label: '25 miles', description: 'Metro area' },
    { value: '50', label: '50 miles', description: 'Regional area' },
    { value: '100', label: '100+ miles', description: 'Multi-city area' },
    { value: 'national', label: 'National', description: 'Nationwide' }
  ]

  // Calculate completion status
  const getCompletionStats = () => {
    const hasServiceArea = data.competitiveProfile.serviceArea.city && data.competitiveProfile.serviceArea.state
    const hasAdvantages = data.competitiveProfile.competitiveAdvantages.length > 0
    const hasCompetitors = data.competitiveProfile.knownCompetitors.length > 0
    
    const completedSections = [hasServiceArea, hasAdvantages, hasCompetitors].filter(Boolean).length
    const totalSections = 3
    
    return {
      hasServiceArea,
      hasAdvantages,
      hasCompetitors,
      completionPercentage: Math.round((completedSections / totalSections) * 100)
    }
  }

  const stats = getCompletionStats()

  // Get advantage icon
  const getAdvantageIcon = (advantage: string) => {
    if (advantage.toLowerCase().includes('price')) return DollarSign
    if (advantage.toLowerCase().includes('quality')) return Award
    if (advantage.toLowerCase().includes('speed') || advantage.toLowerCase().includes('fast')) return Zap
    if (advantage.toLowerCase().includes('service')) return Users
    if (advantage.toLowerCase().includes('location')) return MapPin
    return Star
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-purple-100 rounded-full">
          <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
            <Target className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-purple-700">{industryConfig.name}</span>
        </div>
        
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Competitive Intelligence Configuration
          <HelpTooltip text="Track your competitors and competitive landscape to stay ahead of market changes. We'll monitor competitor activities, new market entrants, pricing changes, and opportunities in your service area." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Define your competitive landscape to receive targeted intelligence about 
          market changes, competitor activities, and opportunities in your area.
        </p>

        {/* Progress Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
          <div className={cn(
            "bg-white rounded-xl p-4 border shadow-sm transition-colors",
            stats.hasServiceArea ? "border-green-200 bg-green-50" : "border-gray-200"
          )}>
            <div className="flex items-center gap-2 mb-2">
              {stats.hasServiceArea ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <MapPin className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-semibold text-sm">Service Area</span>
            </div>
            <div className="text-xs text-gray-600">
              {stats.hasServiceArea ? 'Configured' : 'Define market area'}
            </div>
          </div>
          
          <div className={cn(
            "bg-white rounded-xl p-4 border shadow-sm transition-colors",
            stats.hasAdvantages ? "border-green-200 bg-green-50" : "border-gray-200"
          )}>
            <div className="flex items-center gap-2 mb-2">
              {stats.hasAdvantages ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Award className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-semibold text-sm">Advantages</span>
            </div>
            <div className="text-xs text-gray-600">
              {stats.hasAdvantages ? `${data.competitiveProfile.competitiveAdvantages.length} selected` : 'Select strengths'}
            </div>
          </div>

          <div className={cn(
            "bg-white rounded-xl p-4 border shadow-sm transition-colors",
            stats.hasCompetitors ? "border-green-200 bg-green-50" : "border-gray-200"
          )}>
            <div className="flex items-center gap-2 mb-2">
              {stats.hasCompetitors ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <Users className="w-5 h-5 text-gray-400" />
              )}
              <span className="font-semibold text-sm">Competitors</span>
            </div>
            <div className="text-xs text-gray-600">
              {stats.hasCompetitors ? `${data.competitiveProfile.knownCompetitors.length} identified` : 'Add competitors'}
            </div>
          </div>
        </div>
      </div>

      {/* Service Area Definition */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            Competitive Service Area
          </CardTitle>
          <CardDescription className="text-base">
            Define your primary market area for competitive monitoring and intelligence gathering
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-2">
              <Label htmlFor="service-city" className="flex items-center gap-2 font-medium">
                <Building className="w-4 h-4 text-gray-500" />
                City *
              </Label>
              <Input
                id="service-city"
                value={data.competitiveProfile.serviceArea.city}
                onChange={(e) => updateServiceArea('city', e.target.value)}
                placeholder="Enter city"
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="service-state" className="flex items-center gap-2 font-medium">
                <MapPin className="w-4 h-4 text-gray-500" />
                State *
              </Label>
              <Input
                id="service-state"
                value={data.competitiveProfile.serviceArea.state}
                onChange={(e) => updateServiceArea('state', e.target.value)}
                placeholder="State"
                maxLength={2}
                className="h-12 text-base uppercase"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="service-zip" className="flex items-center gap-2 font-medium">
                <Target className="w-4 h-4 text-gray-500" />
                ZIP Code
              </Label>
              <Input
                id="service-zip"
                value={data.competitiveProfile.serviceArea.zipCode}
                onChange={(e) => updateServiceArea('zipCode', e.target.value)}
                placeholder="12345"
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="competitive-radius" className="flex items-center gap-2 font-medium">
                <Globe className="w-4 h-4 text-gray-500" />
                Monitor Radius
              </Label>
              <Select 
                value={data.competitiveProfile.serviceArea.radius} 
                onValueChange={(value) => updateServiceArea('radius', value)}
              >
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select radius" />
                </SelectTrigger>
                <SelectContent>
                  {radiusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="py-3">
                      <div className="space-y-1">
                        <div className="font-medium">{option.label}</div>
                        <div className="text-sm text-gray-500">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {stats.hasServiceArea && (
            <div className="mt-6 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-purple-800 mb-2">Market Intelligence Activated</div>
                  <div className="text-purple-700">
                    We'll monitor competitive activity in <strong>{data.competitiveProfile.serviceArea.city}, {data.competitiveProfile.serviceArea.state}</strong>
                    {data.competitiveProfile.serviceArea.radius && (
                      <> within a <strong>{radiusOptions.find(r => r.value === data.competitiveProfile.serviceArea.radius)?.label}</strong> radius</>
                    )}, including new business openings, expansions, pricing changes, and market developments.
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Competitive Advantages */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            Your Competitive Advantages
          </CardTitle>
          <CardDescription className="text-base">
            Select your key competitive strengths to help us monitor threats and opportunities in these areas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {industryConfig.competitiveFactors.map((factor) => {
              const isSelected = data.competitiveProfile.competitiveAdvantages.includes(factor)
              const IconComponent = getAdvantageIcon(factor)
              
              return (
                <div
                  key={factor}
                  className={cn(
                    "flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200",
                    isSelected 
                      ? "border-green-500 bg-green-50 shadow-sm" 
                      : "border-gray-200 hover:border-green-300 hover:bg-green-50/30"
                  )}
                  onClick={() => toggleAdvantage(factor)}
                >
                  <Checkbox
                    id={`advantage-${factor}`}
                    checked={isSelected}
                    onChange={() => {}} // Controlled by parent click
                    className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  />
                  <div className="flex items-center gap-3 flex-1">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                      isSelected ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"
                    )}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <Label 
                      htmlFor={`advantage-${factor}`}
                      className="font-medium cursor-pointer flex-1"
                    >
                      {factor}
                    </Label>
                  </div>
                </div>
              )
            })}
          </div>

          {data.competitiveProfile.competitiveAdvantages.length > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-green-800 mb-1">Advantage Monitoring Active</div>
                  <div className="text-sm text-green-700">
                    We'll specifically monitor threats to your <strong>{data.competitiveProfile.competitiveAdvantages.length} selected advantages</strong> and 
                    alert you when competitors make moves that could impact these strengths.
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Known Competitors */}
      <Card className="border-2 hover:border-primary/30 transition-colors duration-300">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            Known Competitors
          </CardTitle>
          <CardDescription className="text-base">
            Add specific competitors you want to monitor for business intelligence and market developments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Add Competitor Input */}
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                value={newCompetitor}
                onChange={(e) => setNewCompetitor(e.target.value)}
                placeholder="Enter competitor name (e.g., 'ABC Auto Body', 'Smith Construction')"
                className="h-12 text-base"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addCompetitor()
                  }
                }}
              />
            </div>
            <Button 
              onClick={addCompetitor}
              disabled={!newCompetitor.trim()}
              className="h-12 px-6"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>

          {/* Competitors List */}
          {data.competitiveProfile.knownCompetitors.length > 0 && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Monitoring List</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.competitiveProfile.knownCompetitors.map((competitor, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-orange-300 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Building className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="font-medium">{competitor}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCompetitor(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                <div className="flex items-start gap-3">
                  <Search className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-orange-800 mb-1">Active Competitor Monitoring</div>
                    <div className="text-sm text-orange-700">
                      We'll track <strong>{data.competitiveProfile.knownCompetitors.length} competitors</strong> for 
                      location changes, service expansions, pricing updates, customer reviews, and strategic moves.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {data.competitiveProfile.knownCompetitors.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <div className="text-gray-600 mb-2">No competitors added yet</div>
              <div className="text-sm text-gray-500">
                Add competitors to enable targeted intelligence monitoring
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Intelligence Preview */}
      {stats.completionPercentage > 50 && (
        <div className="p-8 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-purple-800 mb-3">Competitive Intelligence Preview</h3>
              <p className="text-purple-700 mb-6">
                Based on your competitive configuration, our AI will monitor and alert you about:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-purple-800">
                    <AlertTriangle className="w-5 h-5" />
                    Competitive Threats
                  </div>
                  <ul className="space-y-2 text-sm text-purple-700 ml-7">
                    <li>• New competitors entering your service area</li>
                    <li>• Existing competitors expanding services</li>
                    <li>• Pricing changes from known competitors</li>
                    <li>• Threats to your competitive advantages</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-purple-800">
                    <Lightbulb className="w-5 h-5" />
                    Market Opportunities
                  </div>
                  <ul className="space-y-2 text-sm text-purple-700 ml-7">
                    <li>• Service gaps in your market area</li>
                    <li>• Competitor weaknesses you can exploit</li>
                    <li>• Customer dissatisfaction with competitors</li>
                    <li>• Expansion opportunities in adjacent areas</li>
                  </ul>
                </div>
              </div>

              {data.competitiveProfile.knownCompetitors.length > 0 && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {data.competitiveProfile.knownCompetitors.slice(0, 6).map((competitor) => (
                    <div key={competitor} className="bg-white/70 rounded-xl p-4 border border-purple-200/50">
                      <div className="flex items-start gap-3">
                        <Building className="w-4 h-4 text-purple-700 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-purple-800 text-sm">
                            {competitor}
                          </div>
                          <div className="text-xs text-purple-700 mt-1">
                            Active monitoring enabled
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {data.competitiveProfile.knownCompetitors.length > 6 && (
                    <div className="bg-white/70 rounded-xl p-4 border border-purple-200/50 flex items-center justify-center">
                      <div className="text-center">
                        <div className="font-semibold text-purple-800 text-sm">
                          +{data.competitiveProfile.knownCompetitors.length - 6} more
                        </div>
                        <div className="text-xs text-purple-700">competitors</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-6 p-4 bg-white/50 rounded-xl border border-purple-200/50">
                <div className="text-sm text-purple-700 font-medium">
                  🚀 Almost done! Configure data integration next to complete your intelligence setup.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Status */}
      <div className={cn(
        "p-6 rounded-xl border-2 transition-all duration-300",
        stats.completionPercentage >= 80 ? "bg-green-50 border-green-200" :
        stats.completionPercentage >= 50 ? "bg-yellow-50 border-yellow-200" :
        "bg-gray-50 border-gray-200"
      )}>
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center",
            stats.completionPercentage >= 80 ? "bg-green-100" :
            stats.completionPercentage >= 50 ? "bg-yellow-100" :
            "bg-gray-100"
          )}>
            {stats.completionPercentage >= 80 ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : (
              <Target className="w-6 h-6 text-gray-600" />
            )}
          </div>
          <div>
            <div className="text-lg font-semibold mb-1">
              Competitive Intelligence: {stats.completionPercentage}% Complete
            </div>
            <div className="text-sm text-gray-600">
              {stats.completionPercentage >= 80 && "Ready for next step! "}
              {stats.hasServiceArea && "Service area defined"}{stats.hasServiceArea && (stats.hasAdvantages || stats.hasCompetitors) && " • "}
              {stats.hasAdvantages && `${data.competitiveProfile.competitiveAdvantages.length} advantages selected`}{stats.hasAdvantages && stats.hasCompetitors && " • "}
              {stats.hasCompetitors && `${data.competitiveProfile.knownCompetitors.length} competitors identified`}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}